import type { AriaAttributes, ComponentProps } from 'react'
import type { TooltipProps } from './types'

import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import { cva } from 'class-variance-authority'
import { cloneElement } from 'react'

import { surfaceVariants } from '#components/surface/styles'
import { textVariants } from '#components/text/styles'
import { TextColor, TextSize } from '#components/text/types'
import { cn } from '#lib/cn'

import { TooltipAlign, TooltipSide } from './types'

// Parts (thin BaseUI passthroughs)

export type TooltipRootProps = ComponentProps<typeof BaseTooltip.Root>
export function TooltipRoot(props: TooltipRootProps) {
  return <BaseTooltip.Root {...props} />
}
TooltipRoot.displayName = 'Tooltip.Root'

export type TooltipTriggerProps = ComponentProps<typeof BaseTooltip.Trigger>
export function TooltipTrigger({ ref, ...props }: TooltipTriggerProps) {
  return <BaseTooltip.Trigger ref={ref} {...props} />
}
TooltipTrigger.displayName = 'Tooltip.Trigger'

export type TooltipPortalProps = ComponentProps<typeof BaseTooltip.Portal>
export function TooltipPortal(props: TooltipPortalProps) {
  return <BaseTooltip.Portal {...props} />
}
TooltipPortal.displayName = 'Tooltip.Portal'

export type TooltipPositionerProps = ComponentProps<
  typeof BaseTooltip.Positioner
>
export function TooltipPositioner({
  sideOffset = 5,
  className,
  ref,
  ...props
}: TooltipPositionerProps) {
  return (
    <BaseTooltip.Positioner
      ref={ref}
      sideOffset={sideOffset}
      // z-50 must be on the positioner, not the popup, so the tooltip renders above other stacking contexts
      className={cn('z-50', className)}
      {...props}
    />
  )
}
TooltipPositioner.displayName = 'Tooltip.Positioner'

/** @public — tooltip popup surface CVA variants */
export const tooltipVariants = cva([
  surfaceVariants({ elevation: 'sm' }),
  'inline-flex items-center',
  'px-2 py-1',
  // Cap width so long content wraps instead of spanning the viewport
  'max-w-[400px]',
  // Animation
  'transition-[opacity,scale] duration-200',
  'origin-[var(--transform-origin)]',
  'data-[starting-style]:opacity-0 motion-safe:data-[starting-style]:scale-[0.94]',
  'data-[ending-style]:opacity-0 motion-safe:data-[ending-style]:scale-[0.92]',
  'data-[ending-style]:duration-75',
])

export type TooltipPopupProps = ComponentProps<typeof BaseTooltip.Popup>
export function TooltipPopup({ className, ref, ...props }: TooltipPopupProps) {
  return (
    <BaseTooltip.Popup
      className={cn(tooltipVariants({ className }))}
      ref={ref}
      {...props}
    />
  )
}
TooltipPopup.displayName = 'Tooltip.Popup'

export type TooltipProviderProps = ComponentProps<typeof BaseTooltip.Provider>
export function TooltipProvider(props: TooltipProviderProps) {
  return <BaseTooltip.Provider {...props} />
}
TooltipProvider.displayName = 'Tooltip.Provider'

// Standard open delay for tooltip triggers.
export const TOOLTIP_OPEN_DELAY_MS = 250

// Composed Tooltip

/**
 * Content is capped at a 400px max width (about 70 characters) and wraps.
 * Override via the `maxWidth` prop.
 */
export function Tooltip({
  content,
  hotkey,
  side = TooltipSide.Top,
  align = TooltipAlign.Center,
  sideOffset,
  collisionBoundary,
  maxWidth,
  delay = TOOLTIP_OPEN_DELAY_MS,
  closeDelay,
  closeOnClick,
  open,
  onOpenChange,
  onOpenChangeComplete,
  disabled,
  private: privateContent = false,
  children,
  'aria-expanded': ariaExpanded,
}: TooltipProps) {
  // cloneElement overrides the child's own props, and Base UI's prop merge
  // assigns any own key — including one whose value is `undefined`, which would
  // erase an `aria-expanded` the trigger sets for itself. Forward only when a
  // parent actually stamped one.
  const forwarded: AriaAttributes =
    ariaExpanded === undefined ? {} : { 'aria-expanded': ariaExpanded }

  if (disabled) {
    return cloneElement(children, forwarded)
  }

  const popupStyle = maxWidth === undefined ? undefined : { maxWidth }

  const label = (
    <span
      className={textVariants({
        color: TextColor.Primary,
        size: TextSize.XS,
      })}
    >
      {content}
    </span>
  )

  return (
    <TooltipRoot
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      open={open}
    >
      <TooltipTrigger
        {...forwarded}
        // Base UI keys the trigger by this id but matches the hovered element by
        // its DOM id, so a child that sets its own id must register under it.
        id={(children.props as { id?: string }).id}
        closeDelay={closeDelay}
        closeOnClick={closeOnClick}
        delay={delay}
        render={children}
      />
      <TooltipPortal>
        <TooltipPositioner
          align={align}
          side={side}
          sideOffset={sideOffset}
          collisionBoundary={collisionBoundary}
        >
          <TooltipPopup
            data-private={privateContent || undefined}
            style={popupStyle}
          >
            {hotkey ? (
              <span className="inline-flex items-center gap-1.5 [&_[data-slot=hotkey]]:bg-surface-secondary">
                {label}
                {hotkey}
              </span>
            ) : (
              label
            )}
          </TooltipPopup>
        </TooltipPositioner>
      </TooltipPortal>
    </TooltipRoot>
  )
}
Tooltip.displayName = 'Tooltip'
